import React, { useState, useEffect } from 'react';
import { useUserContext } from '../../store/user_context';

const AddItemToMenu = () => {
  const { user } = useUserContext();
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL;
  const getLookupsEndpoint = import.meta.env.VITE_get_lookups;
  const addItemEndpoint = import.meta.env.VITE_add_item_to_menu;

  useEffect(() => {
    fetchLookups();
  }, []);

  const fetchLookups = async () => {
    try {
      setLoading(true);
      const fullUrl = `${apiUrl}${getLookupsEndpoint}`;
      console.log('Fetching lookups from:', fullUrl);
      
      const response = await fetch(fullUrl);
      if (response.ok) {
        const data = await response.json();
        console.log('Lookup data received:', data);
        
        // Set menus from lookup response
        setRestaurants(data.menus || []);
        
        // Set categories from lookup response
        setCategories(data.categories || []);
      } else {
        console.error('Failed to fetch lookups. Status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurantChange = (e) => {
    setSelectedRestaurant(e.target.value);
    setValidationErrors(prev => ({ ...prev, restaurant: '' }));
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setValidationErrors(prev => ({ ...prev, category: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    switch (name) {
      case 'itemName':
        setItemName(value);
        break;
      case 'itemPrice':
        setItemPrice(value);
        break;
      case 'itemDescription':
        setItemDescription(value);
        break;
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!selectedRestaurant) {
      errors.restaurant = 'يجب اختيار المطعم';
    }

    if (!selectedCategory) {
      errors.category = 'يجب اختيار النوع';
    }

    if (!itemName.trim()) {
      errors.itemName = 'اسم الصنف مطلوب';
    } else if (itemName.trim().length > 100) {
      errors.itemName = 'يجب أن لا يزيد اسم الصنف عن 100 حرف';
    }

    if (!itemPrice || itemPrice === '') {
      errors.itemPrice = 'سعر الصنف مطلوب';
    } else if (isNaN(parseFloat(itemPrice)) || parseFloat(itemPrice) < 0) {
      errors.itemPrice = 'سعر الصنف يجب أن يكون رقماً صحيحاً أكبر من أو يساوي صفر';
    }

    if (itemDescription.trim().length > 200) {
      errors.itemDescription = 'يجب أن لا يزيد الوصف عن 200 حرف';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!user || (!user.userId && !user.id)) {
      setValidationErrors({ submit: 'يجب تسجيل الدخول أولاً' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setValidationErrors({});

    try {
      // Prepare the request payload (matching AddItemDTO structure)
      const requestPayload ={ ItemData: {
        MenuId: parseInt(selectedRestaurant),
        CategoryId: parseInt(selectedCategory),
        ItemName: itemName.trim(),
        ItemPrice: parseFloat(itemPrice),
        ItemDescription: itemDescription.trim() || null,
        UserId: parseInt(user?.userId || user?.id)
      }
    };

      console.log('Sending add item request to:', `${apiUrl}${addItemEndpoint}`);
      console.log('Request payload:', requestPayload);

      // Make API call to add item
      const response = await fetch(`${apiUrl}${addItemEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      setSuccessMessage(result.message || 'تم إضافة الصنف بنجاح');
      
      // Reset form
      setItemName('');
      setItemPrice('');
      setItemDescription('');
      setSelectedCategory('');

    } catch (error) {
      console.error('Error adding item:', error);
      
      if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Failed to fetch')) {
        setValidationErrors({ submit: 'لا يمكن الاتصال بالخادم. تأكد من تشغيل الخادم الخلفي.' });
      } else if (error.message.includes('HTTP 400')) {
        const errorMessage = error.message.replace('HTTP 400: ', '');
        setValidationErrors({ submit: errorMessage || 'خطأ في البيانات المرسلة' });
      } else if (error.message.includes('HTTP 500')) {
        setValidationErrors({ submit: 'خطأ في الخادم. يرجى المحاولة مرة أخرى.' });
      } else {
        setValidationErrors({ submit: error.message || 'حدث خطأ أثناء إضافة الصنف' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="main_content">
      <div className="container">
        <h1 className="page-title">إضافة صنف لقائمة موجودة</h1>
        
        {successMessage && (
          <div className="alert success">
            {successMessage}
          </div>
        )}

        {validationErrors.submit && (
          <div className="alert error">
            {validationErrors.submit}
          </div>
        )}

        {/* Validation Summary */}
        {(() => {
          const errorEntries = Object.entries(validationErrors).filter(([field, error]) => 
            error && field !== 'submit' && error.trim() !== ''
          );
          
          if (errorEntries.length > 0) {
            return (
              <div className="validation-summary-errors" data-valmsg-summary="true" id="validationSummary">
                <ul>
                  {errorEntries.map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        })()}

        <form onSubmit={handleSubmit} className="form-horizontal">
          <div className="form-group">
            <label htmlFor="restaurantSelect">
              <b>اسم المطعم :</b>
            </label>
            <div className="field">
              <select
                id="restaurantSelect"
                value={selectedRestaurant}
                onChange={handleRestaurantChange}
                className="form-control"
              >
                <option value="">من فضلك اختر اسم المطعم</option>
                {restaurants.map((restaurant) => (
                  <option key={restaurant.menuId} value={restaurant.menuId}>
                    {restaurant.menuName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="categorySelect">
              <b>النوع :</b>
            </label>
            <div className="field">
              <select
                id="categorySelect"
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="form-control"
              >
                <option value="">من فضلك اختر النوع</option>
                {categories.map((category) => (
                  <option key={category.categoryId} value={category.categoryId}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="itemName">
              <b>الصنف :</b>
            </label>
            <div className="field">
              <input
                type="text"
                id="itemName"
                name="itemName"
                value={itemName}
                onChange={handleInputChange}
                className="form-control"
                placeholder="أدخل اسم الصنف"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="itemPrice">
              <b>سعر الصنف :</b>
            </label>
            <div className="field">
              <input
                type="number"
                id="itemPrice"
                name="itemPrice"
                value={itemPrice}
                onChange={handleInputChange}
                className="form-control"
                placeholder="أدخل سعر الصنف"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="itemDescription">
              <b>وصف الصنف (اختياري) :</b>
            </label>
            <div className="field">
              <textarea
                id="itemDescription"
                name="itemDescription"
                value={itemDescription}
                onChange={handleInputChange}
                className="form-control"
                placeholder="أدخل وصف الصنف"
                rows="3"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'جاري الحفظ...' : 'حفظ'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default AddItemToMenu;
